import type { Entity } from '@backstage/catalog-model';
import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type { JSX } from 'react';

import {
  createExtensionBlueprint,
  createExtensionDataRef,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';
import { EntityHeaderBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEffect } from 'react';

import type { DynamicSubRoute } from '../types';

import { entityFilterDataRef } from './dataRefs';

export type DynamicRouteProps = () => {
  dynamicGroups?: EntityContentGroupDefinitions;
  dynamicRoutes?: DynamicSubRoute[];
  loading: boolean;
};

export type DynamicSubRouteProps = {
  resolveSubRoutes: (
    result:
      | {
        dynamicGroups?: EntityContentGroupDefinitions;
        routes: DynamicSubRoute[];
      }
      | { loading: true },
    dynamicId: string,
  ) => void;
};

const componentRef = createExtensionDataRef<
  (props: DynamicSubRouteProps) => JSX.Element
>().with({
  id: 'page.catalog.entity.dynamicRoutes',
});

export const DynamicEntityRouteBlueprint = createExtensionBlueprint({
  attachTo: { id: 'entity-layout:catalog/ipen-layout', input: 'dynamicRoutes' },
  dataRefs: {
    component: componentRef,
    filterFunction: entityFilterDataRef,
  },
  * factory(
    params: {
      filter?: (entity: Entity) => boolean;
      useDynamicSubRoutes: DynamicRouteProps;
    },
    { node },
  ) {
    const loader =
      async () =>
        ({ resolveSubRoutes }: DynamicSubRouteProps) => {
          const { dynamicGroups, dynamicRoutes, loading } =
            params.useDynamicSubRoutes();

          useEffect(() => {
            resolveSubRoutes(
              loading
                ? { loading }
                : { dynamicGroups, routes: dynamicRoutes ?? [] },
              node.spec.id,
            );
          }, [resolveSubRoutes, dynamicRoutes, loading, dynamicGroups]);

          // eslint-disable-next-line @eslint-react/jsx-no-useless-fragment
          return <></>;
        };

    yield componentRef(ExtensionBoundary.lazyComponent(node, loader));
    if (params.filter) {
      yield EntityHeaderBlueprint.dataRefs.filterFunction(params.filter);
    }
  },
  kind: 'dynamic-entity-route',
  output: [componentRef, entityFilterDataRef.optional()],
});
